import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { OfflineDbService } from './offline-db.service';
import { OfflineCryptoService } from './offline-crypto.service';
import { buildContentKey, getCurrentSchoolSubdomain, getCurrentUserId } from './offline-cache-key';

// Small, generic cache for the app's OWN bootstrap calls (School/getschoolconfig,
// offline/session) — NOT the explicit-save machinery in offline-save.service.ts. Those two
// calls gate whether the router/banner render at all, so without a fallback, a reload while
// offline hits a hard "startupFailed" wall before the user's already-saved lesson content
// ever gets a chance to serve from its own cache. Deliberately simpler than the
// OFFLINE_ROUTE_ALLOWLIST machinery: no explicit-save requirement (D2 doesn't apply — this
// isn't lesson content, it's app infrastructure), no role gate (both responses are
// non-sensitive and identical for admin and student). Always caches on success for any
// logged-in user, regardless of the offline-mode feature flag — this is a baseline resiliency
// fix, not part of the opt-in save-a-lesson feature. Reads the token straight from
// localStorage rather than injecting UseraccessserviceService, which would be a circular
// dependency (that service already injects OfflineSessionService, a consumer of this one).
// Kinds whose data is PUBLIC (served to anyone, signed in or not - School/getschoolconfig has
// no [Authorize] and is what the login page itself loads). Those also get a plain localStorage
// copy, which offline startup falls back to when the encrypted copy cannot be read: the
// encrypted copy is per user and depends on a CryptoKey kept in IndexedDB - the fragile part on
// WebKit - and without the school config startup fails outright ("SOMETHING WENT WRONG").
// Same reasoning, same fallback as the offline-session mirror in offline-session.service.ts.
// localStorage is per origin and each school is its own subdomain, so this is per school.
const PUBLIC_MIRROR_KINDS = new Set(['schoolconfig']);
const mirrorKey = (kind: string) => `impata.offline.public.${kind}`;

@Injectable({
  providedIn: 'root',
})
export class OfflineBootstrapCacheService {
  constructor(private offlineDb: OfflineDbService, private offlineCrypto: OfflineCryptoService) {}

  private currentIdentity(): { school: string; user: number } | null {
    const school = getCurrentSchoolSubdomain();
    const token = localStorage.getItem(environment.userTokenStore);
    const user = getCurrentUserId(token);
    if (!school || !user) return null;
    return { school, user };
  }

  async cache(kind: string, data: unknown): Promise<void> {
    // Before the identity check: public data needs no signed-in user to be worth keeping.
    if (PUBLIC_MIRROR_KINDS.has(kind)) {
      try {
        localStorage.setItem(mirrorKey(kind), JSON.stringify(data));
      } catch {
        /* private mode or quota - the encrypted copy below still applies */
      }
    }

    const identity = this.currentIdentity();
    if (!identity) return; // not logged in (or subdomain unresolvable) — nothing to scope a key to
    const { school, user } = identity;

    const deviceKey = await this.offlineCrypto.getOrCreateDeviceKey();
    const plaintext = new TextEncoder().encode(JSON.stringify(data)).buffer;
    const { iv, ciphertext } = await this.offlineCrypto.encryptBlob(deviceKey, plaintext);
    const key = buildContentKey(school, user, kind, 'bootstrap', 'unversioned');

    await this.offlineDb.putContent({
      key,
      school,
      user,
      kind,
      resourceId: 'bootstrap',
      version: 'unversioned',
      iv,
      ciphertext,
      savedAt: Date.now(),
      bytes: plaintext.byteLength,
    });
  }

  async getCached<T>(kind: string): Promise<T | null> {
    const encrypted = await this.getEncrypted<T>(kind).catch((err) => {
      console.error(`[offline] could not read cached bootstrap data (${kind})`, err);
      return null;
    });
    return encrypted ?? this.getMirror<T>(kind);
  }

  private async getEncrypted<T>(kind: string): Promise<T | null> {
    const identity = this.currentIdentity();
    if (!identity) return null;
    const { school, user } = identity;

    const key = buildContentKey(school, user, kind, 'bootstrap', 'unversioned');
    const record = await this.offlineDb.getContent(key);
    if (!record) return null;

    try {
      const deviceKey = await this.offlineCrypto.getOrCreateDeviceKey();
      const plaintext = await this.offlineCrypto.decryptBlob(deviceKey, record.iv, record.ciphertext);
      return JSON.parse(new TextDecoder().decode(plaintext)) as T;
    } catch (err) {
      console.error(`[offline] failed to decrypt cached bootstrap data (${kind})`, err);
      return null;
    }
  }

  private getMirror<T>(kind: string): T | null {
    if (!PUBLIC_MIRROR_KINDS.has(kind)) return null;
    try {
      const raw = localStorage.getItem(mirrorKey(kind));
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }
}
